import {
	ConnectorLocator,
	RestRequestPayload,
	RestServerConnector
} from "@com.mgmtp.a12.utils/utils-connector/lib/main";

export class GatewayTimeoutError extends Error {
	constructor(message: string, options?: ErrorOptions) {
		super(message, options);
		this.name = "GatewayTimeoutError";
	}
}

export class HttpResponseError extends Error {
	constructor(
		message: string,
		readonly status: number
	) {
		super(message);
		this.name = "HttpResponseError";
	}
}

export class UnexpectedServerResponseError extends Error {
	constructor(message: string, options?: ErrorOptions) {
		super(message, options);
		this.name = "UnexpectedServerResponseError";
	}
}

/**
 * Fetches the json data of a response for a given request.
 * @param request
 * @returns The response of the request as base64
 */
export async function fetchFromServerConnectorAsBase64(
	request: RestRequestPayload,
	format: "ascii" | "utf8" = "utf8"
): Promise<string | undefined> {
	const response = await (ConnectorLocator.getInstance().getServerConnector() as RestServerConnector).fetchData(
		request
	);

	/**
	 * NOTE: The RestServerConnecter uses an error filter that will always reject
	 * if the fetch `response.ok` is `false`. Therefore, the following code will
	 * not be reached.
	 *
	 * However, we cannot guarantee this for the future therefore we will keep it.
	 */
	if (!response.ok) {
		if (isGatewayTimeoutError(response)) {
			throw new GatewayTimeoutError("Gateway Timeout");
		}
		throw new HttpResponseError(response.statusText, response.status);
	}

	let binary = "";
	if (format === "ascii") {
		binary = await response.text();
	} else {
		// Arraybuffer umwandeln zu Base64
		const bytes = new Uint8Array(await response.arrayBuffer());
		for (let i = 0; i < bytes.byteLength; i++) {
			binary += String.fromCharCode(bytes[i]);
		}
	}
	return window.btoa(binary);
}

/**
 * Fetches the json data of a response for a given request.
 * @param request
 * @returns The response of the request as json
 */
export async function fetchFromServerConnector(request: RestRequestPayload): Promise<unknown> {
	const response = await (ConnectorLocator.getInstance().getServerConnector() as RestServerConnector).fetchData(
		request
	);

	/**
	 * NOTE: The RestServerConnecter uses an error filter that will always reject if the fetch
	 * `response.ok` is `false`. Therefore, the following code will not be reached.
	 *
	 * However, we cannot guarantee this for the future therefore we will keep it.
	 */
	if (!response.ok) {
		if (isGatewayTimeoutError(response)) {
			throw new GatewayTimeoutError("Gateway Timeout");
		}
		throw new HttpResponseError(response.statusText, response.status);
	}

	return await response.json();
}

export interface RetryOptions {
	attempts: number;
	delayMs: number;
	shouldRetryResponse?: (response: unknown) => boolean;
	// Keep this injectable so existing restHelper spies remain effective.
	fetchRequest?: (request: RestRequestPayload) => Promise<unknown>;
}

export async function fetchFromServerConnectorWithRetry(
	request: RestRequestPayload,
	{ attempts, delayMs, shouldRetryResponse = () => false, fetchRequest = fetchFromServerConnector }: RetryOptions
): Promise<unknown> {
	if (attempts < 1) {
		throw new RangeError("The retry attempt count must be at least one.");
	}

	let lastError: unknown;

	for (let attempt = 1; attempt <= attempts; attempt++) {
		try {
			const response = await fetchRequest(request);
			if (!shouldRetryResponse(response)) {
				return response;
			}
			lastError = new GatewayTimeoutError("Gateway timeout reported in the JSON-RPC response.");
		} catch (error) {
			if (!isGatewayTimeoutError(error)) {
				throw error;
			}
			lastError = error;
		}

		if (attempt < attempts) {
			await new Promise(resolve => setTimeout(resolve, delayMs));
		}
	}

	throw new GatewayTimeoutError("The request failed after all retry attempts.", { cause: lastError });
}

export function isGatewayTimeoutResponse(response: unknown): boolean {
	return (
		Array.isArray(response) &&
		response.some(entry => {
			if (!entry || typeof entry !== "object") {
				return false;
			}

			return isGatewayTimeoutError((entry as { error?: unknown }).error);
		})
	);
}

export function isGatewayTimeoutError(error: unknown): boolean {
	if (error instanceof GatewayTimeoutError) {
		return true;
	}

	if (error instanceof Error && /gateway timeout|\b504\b/i.test(error.message)) {
		return true;
	}

	if (!error || typeof error !== "object") {
		return false;
	}

	const message = (error as { message?: unknown }).message;
	if (typeof message === "string" && /gateway timeout|\b504\b/i.test(message)) {
		return true;
	}

	const code = (error as { code?: unknown }).code;
	const status = (error as { status?: unknown }).status;
	const response = (error as { response?: unknown }).response;
	const responseStatus =
		response && typeof response === "object" ? (response as { status?: unknown }).status : undefined;
	return (
		code === 504 ||
		code === "504" ||
		status === 504 ||
		status === "504" ||
		responseStatus === 504 ||
		responseStatus === "504"
	);
}

export function isDocumentNotFoundError(error: unknown): boolean {
	if (!(error instanceof UnexpectedServerResponseError)) {
		return false;
	}

	return hasDocumentNotFoundMarker(error.cause);
}

function hasDocumentNotFoundMarker(value: unknown): boolean {
	if (!value || typeof value !== "object") {
		return false;
	}

	const candidate = value as {
		code?: unknown;
		message?: unknown;
		status?: unknown;
		data?: unknown;
		error?: unknown;
		description?: unknown;
		default?: unknown;
	};
	const code = typeof candidate.code === "string" ? candidate.code.toUpperCase() : candidate.code;
	const message = [candidate.message, candidate.description, candidate.default]
		.filter((part): part is string => typeof part === "string")
		.join(" ");

	if (
		candidate.status === 404 ||
		candidate.status === "404" ||
		code === "NOT_FOUND" ||
		code === "DOCUMENT_NOT_FOUND" ||
		(typeof code === "string" && code.endsWith("_NOT_FOUND")) ||
		/((document|document\s+reference|docref).*\b(not found|does not exist)\b|\b(not found|does not exist)\b.*(document|document\s+reference|docref))/i.test(
			message
		)
	) {
		return true;
	}

	return (
		hasDocumentNotFoundMarker(candidate.data) ||
		hasDocumentNotFoundMarker(candidate.description) ||
		hasDocumentNotFoundMarker(candidate.error)
	);
}
